import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { tasksService, usersService, projectsService } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, Calendar, User, Plus, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";

const STATUSES = ["To Do", "In Progress", "In Review", "Blocked"];
const PRIORITIES = ["Urgent", "High", "Medium", "Low"];

const PRIORITY_CONFIG = {
  "Urgent": { bg: "bg-red-100", text: "text-red-700", border: "border-red-300" },
  "High": { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300" },
  "Medium": { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300" },
  "Low": { bg: "bg-green-100", text: "text-green-700", border: "border-green-300" }
};

const NewTaskPanel = ({ isOpen, onClose, onSuccess, initialStatus, projects, teamMembers }) => {
  const [loading, setLoading] = useState(false);
  const [searchMembers, setSearchMembers] = useState("");
  const [taskProjects, setTaskProjects] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState("");
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      title: "",
      description: "",
      projectId: "",
      status: initialStatus || "To Do",
      priority: "Medium",
      assignedTo: "",
      startDate: "",
      dueDate: ""
    }
  });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await projectsService.getAll();
        setTaskProjects(data.filter(p => p.status !== "Archived"));
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setSubtasks([...subtasks, { id: Date.now().toString(), title: newSubtask, completed: false }]);
      setNewSubtask("");
    }
  };

  const handleRemoveSubtask = (id) => {
    setSubtasks(subtasks.filter(st => st.id !== id));
  };

  const filteredMembers = teamMembers?.filter(member =>
    member.name?.toLowerCase().includes(searchMembers.toLowerCase())
  ) || [];

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await tasksService.create({
        ...data,
        subtasks,
        comments: [],
        attachments: []
      });
      toast({
        title: "Task created",
        description: "Task has been created successfully",
        variant: "success"
      });
      reset();
      setSubtasks([]);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error creating task:", error);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-screen w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-navy">New Task</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="title">Task Name *</Label>
            <Input
              id="title"
              {...register("title", { required: "Task name is required" })}
              placeholder="Enter task name"
              className={cn(errors.title && "border-red-500")}
            />
            {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Enter task description"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Project</Label>
            <select
              {...register("projectId")}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
            >
              <option value="">No Project</option>
              {taskProjects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <select
                {...register("status")}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <div className="flex gap-1">
                {PRIORITIES.map(priority => (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => setValue("priority", priority)}
                    className={cn(
                      "flex-1 py-2 px-1 rounded text-xs font-medium transition-all",
                      watch("priority") === priority
                        ? PRIORITY_CONFIG[priority].bg + " " + PRIORITY_CONFIG[priority].text + " border-2 " + PRIORITY_CONFIG[priority].border
                        : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                    )}
                  >
                    {priority === "Urgent" ? "🔴" : priority === "High" ? "🟠" : priority === "Medium" ? "🟡" : "🟢"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign To</Label>
            <select
              {...register("assignedTo")}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">Unassigned</option>
              {teamMembers?.map(member => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <div className="relative">
                <Input
                  id="startDate"
                  type="date"
                  {...register("startDate")}
                  className="pl-10"
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <div className="relative">
                <Input
                  id="dueDate"
                  type="date"
                  {...register("dueDate")}
                  className="pl-10"
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Subtasks</Label>
            <div className="space-y-2">
              {subtasks.map(subtask => (
                <div key={subtask.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <CheckSquare className="w-4 h-4 text-gray-400" />
                  <span className="flex-1 text-sm">{subtask.title}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleRemoveSubtask(subtask.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add a subtask..."
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddSubtask())}
                className="flex-1"
              />
              <Button type="button" size="sm" onClick={handleAddSubtask}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-navy hover:bg-navy-light">
              {loading ? "Creating..." : "Create Task"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default NewTaskPanel;